'use client';

import type { CommentLabelSeverity } from '@/types/annotations/types';
import { AlertTriangle, CheckCircle, FlagTriangleLeft, Info, MessageSquare, X } from 'lucide-react';
import { useState } from 'react';

import { Button, EscalationIcon } from '@/components/era-ui';
import { Badge } from './legacy/Badge';

const getSeverityIcon = (severity: string) => {
  switch (severity) {
    case 'minor':
      return <Info className="h-3 w-3 text-blue-500" />;
    case 'major':
      return <AlertTriangle className="h-3 w-3 text-red-500" />;
    case 'escalation':
      return <EscalationIcon />;
    case 'praise':
      return <FlagTriangleLeft className="h-3 w-3 text-green-500" />;
    default:
      return <MessageSquare className="h-3 w-3 text-gray-500" />;
  }
};

export default function Pill<T extends { label: string; isEditable: boolean; severity?: CommentLabelSeverity }>({ pill, onRemove, onUpdate, children }: { pill: T; onRemove: (pill: T) => void; onUpdate: (e: React.FormEvent<HTMLFormElement>) => void; children: React.ReactNode }) {
  const [isEdit, setIsEdit] = useState(false);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setIsEdit(false);
        onUpdate(e);
      }}
      className="p-0 items-center border-none justify-center text-body-md text-gray-700 border"
    >
      <fieldset disabled={!pill.isEditable}>
        <Badge variant="secondary" className="flex items-center py-0 gap-1 w-full">
          {isEdit ? (
            <>
              <input
                name="label"
                type="text"
                defaultValue={pill.label}
                className="bg-white p-1"
              />
              <Button type="submit" variant="tertiary" className="hover:bg-transparent active:bg-transparent">
                <CheckCircle className="h-3 w-3 text-gray-500 hover:text-green-600" />
              </Button>
              {/* keeping this here just so that the event handler is backward compatible */}
              <input name="severity" type="text" defaultValue={pill.severity} className="hidden" />
            </>
          ) : (
            <Button disabled={!pill.isEditable} variant="tertiary" onClick={() => setIsEdit(true)} className="hover:bg-transparent active:bg-transparent">
              {pill.severity ? getSeverityIcon(pill.severity?.toLowerCase()) : null}
              <span>{pill.label}</span>
            </Button>
          )}
          <Button
            className="bg-transparent hover:bg-transparent active:bg-transparent shadow-none p-0"
            variant="tertiary"
            aria-label={`Remove ${pill.label}`}
            disabled={!pill.isEditable}
            onClick={() => onRemove(pill)}
          >
            {children || <X className="h-3 w-3 text-gray-500 hover:text-red-600" />}
          </Button>
        </Badge>
      </fieldset>
    </form>
  );
}
